import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Autocomplete, Paper, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { locationAPI } from '../services/api';
import { Location } from '../types';
import { debounce } from '@mui/material/utils';

interface SearchBarProps {
  variant?: 'hero' | 'standard';
}

const SearchBar: React.FC<SearchBarProps> = ({ variant = 'hero' }) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLocations = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const locations = await locationAPI.search(query);
        setOptions(locations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchLocations(inputValue);
  }, [inputValue, fetchLocations]);

  const handleLocationSelect = (location: Location | null) => {
    if (location) {
      navigate(`/search?location=${encodeURIComponent(location.name)}`);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputValue) {
      navigate(`/search?location=${encodeURIComponent(inputValue)}`);
    }
  };

  const isHero = variant === 'hero';

  return (
    <Paper
      elevation={0}
      sx={{
        p: isHero ? 3 : 1,
        background: isHero 
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        backdropFilter: isHero ? 'blur(20px)' : 'none',
        borderRadius: isHero ? 4 : 3,
        border: isHero ? '1px solid rgba(255, 255, 255, 0.8)' : '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: isHero 
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5)' 
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: isHero ? 'translateY(-2px)' : 'none',
          boxShadow: isHero 
            ? '0 32px 64px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.6)'
            : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }
      }}
    >
      <Autocomplete
        options={options}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onChange={(_, newValue) => handleLocationSelect(newValue as Location)}
        freeSolo
        PaperComponent={(props) => (
          <Paper 
            {...props} 
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            }} 
          />
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={isHero ? "Search for fun by location..." : "Enter a city or country..."}
            variant="standard"
            fullWidth
            onKeyPress={handleKeyPress}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon 
                    sx={{ 
                      color: isHero ? '#6366f1' : '#6b7280',
                      fontSize: isHero ? '1.5rem' : '1.25rem',
                      transition: 'color 0.3s ease',
                    }} 
                  />
                </InputAdornment>
              ),
              sx: {
                fontSize: isHero ? '1.25rem' : '1rem',
                fontWeight: isHero ? 500 : 400,
                color: '#1f2937',
                '& input::placeholder': {
                  color: isHero ? '#6b7280' : '#9ca3af',
                  opacity: 1,
                  fontSize: isHero ? '1.25rem' : '1rem',
                  fontWeight: isHero ? 400 : 300,
                },
                '& input': {
                  py: isHero ? 2 : 0.5,
                  px: 1,
                },
              },
            }}
          />
        )}
      />
    </Paper>
  );
};

export default SearchBar;